/**
 * @file staticDomain.ts
 * @author Rapoo Device SDK Team
 * @date 2026-06-22
 * @description CH585M 静态灯效领域描述
 */

import {
  LightingDomainDescriptor,
  LightingDomainType,
  LightingFeatureFlag,
} from '../../../../../types/lightingDomain'
import { getAddress } from '../../../../../utils/common'
import { ADDRESS_CH585M } from '../../../protocols/address'
import { genrateStaticLightEffectPresetAddressLevel } from '../../../protocols/addressGenerators'
import { convertASCIIToStr } from '../../../../../utils/StringUtils'
import { sanitizeLightingLevel } from '../../../../shared/feature-impls/keyboard-lighting/lighting'

export const staticDomain: LightingDomainDescriptor = {
  type: LightingDomainType.Static,
  configSize: 8,
  supportedFeatures:
    LightingFeatureFlag.Brightness |
    LightingFeatureFlag.SingleColor |
    LightingFeatureFlag.Gradient |
    LightingFeatureFlag.KeyMap |
    LightingFeatureFlag.Name,

  addressResolver: {
    getBaseAddr: (_configIndex, itemIndex) => {
      return getAddress(ADDRESS_CH585M.STATIC_LIGHT_TYPE, itemIndex)
    },
    getFieldAddr: (field, _configIndex, itemIndex) => {
      const fieldOffsetMap: Record<string, number> = {
        brightness: 1,
        gradientDirection: 0,
        isColorful: 4,
      }
      const offset = fieldOffsetMap[field] ?? 0
      return getAddress(genrateStaticLightEffectPresetAddressLevel(offset), itemIndex)
    },
  },

  parser: {
    parse: (buffer, index) => ({
      index,
      domainType: LightingDomainType.Static,
      gradientDirection: buffer[0],
      brightness: sanitizeLightingLevel(buffer[1]),
      isColorful: buffer[4] === 0,
      rgbGroup: [2, 3].includes(buffer[0])
        ? [
            { r: buffer[2], g: buffer[3], b: buffer[4] },
            { r: buffer[5], g: buffer[6], b: buffer[7] },
          ]
        : undefined,
      rgb: [1].includes(buffer[0]) ? { r: buffer[5], g: buffer[6], b: buffer[7] } : undefined,
      buffer,
    }),
    serialize: (config) => {
      const buf = new Uint8Array(8)
      buf[0] = config.gradientDirection ?? 1
      buf[1] = sanitizeLightingLevel(config.brightness)
      if (buf[0] === 1) {
        buf[2] = 0xff
        buf[3] = 0xff
        buf[4] = config.isColorful ? 0x00 : 0x01
        buf[5] = config.rgb?.r ?? 0xff
        buf[6] = config.rgb?.g ?? 0xff
        buf[7] = config.rgb?.b ?? 0xff
      } else if ([2, 3].includes(buf[0])) {
        const start = config.rgbGroup?.[0] ?? { r: 255, g: 255, b: 255 }
        const end = config.rgbGroup?.[1] ?? { r: 0, g: 0, b: 0 }
        buf[2] = start.r
        buf[3] = start.g
        buf[4] = start.b
        buf[5] = end.r
        buf[6] = end.g
        buf[7] = end.b
      }
      return buf
    },
  },

  hasCustomEffects: true,
  customEffectResolver: {
    nameAddress: ADDRESS_CH585M.STATIC_LIGHT_EFFECT_ADDRESS_NAME_LEVEL,
    readSize: 0x1f,
    parseName: (data) => {
      const nameBuffer = new Array<string>(0x0c)
      for (let i = 0; i < 0x0c; i++) {
        nameBuffer[i] = data.getUint8(4 + i).toString(16)
      }
      const name = convertASCIIToStr(nameBuffer)
      return name || '0'
    },
    parseType: (data) => data.getUint8(4 + 0x0c),
  },

  cacheTags: (index, configIndex) => [`light:static:${index}`, `cfg:${configIndex}`],
}
